import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Marker, Polyline } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import type { LatLngExpression } from "leaflet";
import { getShipPosition, bearingDegrees } from "./world-map-utils";
import type { WorldMapRoute } from "./world-map-utils";
import { createShipMarkerIcon } from "./world-map-ship-marker";
import {
    createRouteDestinationArrivalIcon,
    createRouteOriginIcon,
} from "./world-map-route-endpoint-icons";
import "react-leaflet-cluster/dist/assets/MarkerCluster.css";
import "react-leaflet-cluster/dist/assets/MarkerCluster.Default.css";

export interface WorldMapRoutesAndMarkersProps {
    routes: WorldMapRoute[];
    progressById: Record<string, number>;
    selectedRouteId: string | null;
    isContainerActive: (containerId: string) => boolean;
    onMarkerClick: (containerId: string, position: [number, number]) => void;
}

export function WorldMapRoutesAndMarkers({
    routes,
    progressById,
    selectedRouteId,
    isContainerActive,
    onMarkerClick,
}: WorldMapRoutesAndMarkersProps) {
    const { t } = useTranslation(["pages:containers"]);

    return (
        <>
            {/* Route lines - only for selected route */}
            {selectedRouteId && routes
                .filter(({ c }) => c.id === selectedRouteId)
                .map(({ c, coordinates, color }) => {
                    const isActive = isContainerActive(c.id);
                    const coreWeight = isActive ? 4 : 3;
                    const glowWeight = isActive ? 9 : 7;
                    return (
                        <Fragment key={`line-${c.id}`}>
                            <Polyline
                                positions={coordinates}
                                pathOptions={{
                                    color,
                                    weight: glowWeight,
                                    opacity: isActive ? 0.28 : 0.2,
                                    lineCap: "round",
                                    lineJoin: "round",
                                }}
                            />
                            <Polyline
                                positions={coordinates}
                                pathOptions={{
                                    color,
                                    weight: coreWeight,
                                    opacity: isActive ? 0.95 : 0.86,
                                    lineCap: "round",
                                    lineJoin: "round",
                                }}
                            />
                        </Fragment>
                    );
                })}

            {/* Estremi rotta: partenza (ancora) e arrivo (pin con spunta) — solo rotta selezionata */}
            {selectedRouteId &&
                routes
                    .filter(({ c }) => c.id === selectedRouteId)
                    .map(({ c, from, to, color }) => {
                        const [fa, fo] = from;
                        const [ta, to_] = to;
                        if (
                            !Number.isFinite(fa) ||
                            !Number.isFinite(fo) ||
                            !Number.isFinite(ta) ||
                            !Number.isFinite(to_)
                        ) {
                            return null;
                        }
                        const degenerate =
                            Math.hypot(ta - fa, to_ - fo) < 1e-5;
                        if (degenerate) return null;
                        return (
                            <Fragment key={`route-endpoints-${c.id}`}>
                                <Marker
                                    position={from}
                                    icon={createRouteOriginIcon(color)}
                                    interactive={false}
                                    zIndexOffset={450}
                                    title={t("worldMap.routeOrigin", {
                                        defaultValue: "Departure (origin port)",
                                    })}
                                />
                                <Marker
                                    position={to}
                                    icon={createRouteDestinationArrivalIcon(color)}
                                    interactive={false}
                                    zIndexOffset={450}
                                    title={t("worldMap.routeDestination", {
                                        defaultValue: "Arrival (destination port)",
                                    })}
                                />
                            </Fragment>
                        );
                    })}

            {/* Ship markers (clustered for performance) */}
            <MarkerClusterGroup>
                {routes.map(({ c, coordinates }) => {
                    const t = progressById[c.id] ?? 0;
                    const shipPosResult = c.currentPosition
                        ? ([c.currentPosition.lat, c.currentPosition.lng] as [number, number])
                        : getShipPosition(coordinates, t);
                    if (!shipPosResult) return null;
                    const [lat, lng] = shipPosResult;
                    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
                    const shipPos = shipPosResult as LatLngExpression;

                    const destination = coordinates[coordinates.length - 1];
                    const bearing = destination ? bearingDegrees([lat, lng], destination) : 0;
                    const isActive = isContainerActive(c.id);
                    const markerIcon = createShipMarkerIcon({ isActive, bearing });

                    return (
                        <Marker
                            key={`route-${c.id}`}
                            position={shipPos}
                            icon={markerIcon}
                            zIndexOffset={isActive ? 1000 : 0}
                            eventHandlers={{
                                click: () => {
                                    onMarkerClick(c.id, shipPosResult);
                                },
                            }}
                        />
                    );
                })}
            </MarkerClusterGroup>
        </>
    );
}
